import type { BasemapGeometry } from "./manifest";

/**
 * A sphere's six cube faces in S2's layout, through the equal-angle projection. Face 0 looks
 * at the cube frame's +x, face 1 at +y, face 2 at +z, then their opposites. On a face, s runs 0 to 1
 * left to right and t runs 0 to 1 bottom to top, and the face plane's u is tan(π/4 · (2s − 1)).
 * A pixel's row 0 is the face's top.
 */

export type Vector3 = [number, number, number];
type Orientation = Extract<BasemapGeometry, { kind: "sphere" }>["orientation"];

export const FACE_COUNT = 6;

const QUARTER_PI = Math.PI / 4;

/** A face's plane coordinates to the direction it points at, unnormalized. */
export function faceUvToDirection(face: number, u: number, v: number): Vector3 {
  switch (face) {
    case 0:
      return [1, u, v];
    case 1:
      return [-u, 1, v];
    case 2:
      return [-u, -v, 1];
    case 3:
      return [-1, -v, -u];
    case 4:
      return [v, -1, -u];
    default:
      return [v, u, -1];
  }
}

/** The face a direction points into. */
export function faceOf([x, y, z]: Vector3): number {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const az = Math.abs(z);
  if (ax >= ay && ax >= az) return x > 0 ? 0 : 3;
  if (ay >= az) return y > 0 ? 1 : 4;
  return z > 0 ? 2 : 5;
}

/** Where a direction lands on a face's plane. */
export function directionToFaceUv(face: number, [x, y, z]: Vector3): [number, number] {
  switch (face) {
    case 0:
      return [y / x, z / x];
    case 1:
      return [-x / y, z / y];
    case 2:
      return [-x / z, -y / z];
    case 3:
      return [z / x, y / x];
    case 4:
      return [z / y, -x / y];
    default:
      return [-y / z, -x / z];
  }
}

export const uToS = (u: number) => (Math.atan(u) / QUARTER_PI + 1) / 2;
export const sToU = (s: number) => Math.tan(QUARTER_PI * (2 * s - 1));

/** A direction in the cube's frame to its face and face position. */
export function directionToFaceSt(direction: Vector3): { face: number; s: number; t: number } {
  const face = faceOf(direction);
  const [u, v] = directionToFaceUv(face, direction);
  return { face, s: uToS(u), t: uToS(v) };
}

/** The center of pixel (x, y) of a face at a level, as a direction in the cube's frame. */
export function pixelDirection(face: number, level: number, x: number, y: number): Vector3 {
  const across = 2 ** level;
  return faceUvToDirection(face, sToU((x + 0.5) / across), sToU(1 - (y + 0.5) / across));
}

/** A longitude and latitude in degrees, to a unit direction with x toward 0° 0° and z north. */
export function lonLatToDirection(longitude: number, latitude: number): Vector3 {
  const lon = (longitude * Math.PI) / 180;
  const lat = (latitude * Math.PI) / 180;
  return [Math.cos(lat) * Math.cos(lon), Math.cos(lat) * Math.sin(lon), Math.sin(lat)];
}

/** A direction, normalized or not, to longitude and latitude in degrees. */
export function directionToLonLat([x, y, z]: Vector3): [number, number] {
  return [(Math.atan2(y, x) * 180) / Math.PI, (Math.atan2(z, Math.hypot(x, y)) * 180) / Math.PI];
}

/** Turns a direction in the world's frame into the cube's. */
export function toCube(orientation: Orientation, [x, y, z]: Vector3): Vector3 {
  const [a, b, c] = orientation;
  return [
    a[0] * x + a[1] * y + a[2] * z,
    b[0] * x + b[1] * y + b[2] * z,
    c[0] * x + c[1] * y + c[2] * z,
  ];
}

/** Turns a direction in the cube's frame back into the world's. */
export function toWorld(orientation: Orientation, [x, y, z]: Vector3): Vector3 {
  const [a, b, c] = orientation;
  return [
    a[0] * x + b[0] * y + c[0] * z,
    a[1] * x + b[1] * y + c[1] * z,
    a[2] * x + b[2] * y + c[2] * z,
  ];
}

/** S2's own orientation: the cube frame is the world's. */
export const IDENTITY_ORIENTATION = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
] as const satisfies Orientation;
